import React, { useEffect, useState } from 'react';
import { PlusCircle, Search, Users, UserX, UserCheck, ShieldAlert, Award, KeyRound, Lock, Check, UserPlus, X, Trash2 } from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import { Customer } from '../types/index.js';
import { maskCPF, maskCNPJ, maskPhone } from '../utils/masks.js';

export const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBonusModalOpen, setIsBonusModalOpen] = useState(false);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Form states
  const [type, setType] = useState<'PJ' | 'PF'>('PJ');
  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [document, setDocument] = useState('');
  const [email, setEmail] = useState('');
  const [responsibleName, setResponsibleName] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Users modal states
  const [customerUsers, setCustomerUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // New user inside modal states
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'customer_admin' | 'customer_user'>('customer_admin');
  const [savingNewUser, setSavingNewUser] = useState(false);

  // Bonus form states
  const [bonusAmount, setBonusAmount] = useState(5);
  const [bonusReason, setBonusReason] = useState('Bônus comercial concedido pelo Master');

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const res = await api.getCustomers();
      setCustomers(res.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password && password.length < 6) {
      alert('A senha de acesso deve conter no mínimo 6 caracteres.');
      return;
    }
    if (password && password !== confirmPassword) {
      alert('As senhas digitadas não coincidem.');
      return;
    }

    setSubmitting(true);
    try {
      const payload: any = {
        type,
        email,
        responsibleName: responsibleName || (type === 'PJ' ? legalName : legalName),
      };

      if (type === 'PJ') {
        if (legalName) payload.legalName = legalName;
        if (tradeName) payload.tradeName = tradeName;
        if (document) payload.cnpj = document;
      } else {
        if (legalName) payload.fullName = legalName;
        if (document) payload.cpf = document;
      }

      if (phone) payload.phone = phone;
      if (mobile) payload.mobile = mobile;
      if (password) payload.password = password;

      await api.createCustomer(payload);
      setIsModalOpen(false);
      setLegalName('');
      setTradeName('');
      setDocument('');
      setEmail('');
      setResponsibleName('');
      setPhone('');
      setMobile('');
      setPassword('');
      setConfirmPassword('');
      loadCustomers();
    } catch (err: any) {
      alert(err.message || 'Falha ao criar cliente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenUsersModal = async (cust: Customer) => {
    setSelectedCustomer(cust);
    setSelectedCustomerId(cust.id);
    setIsUsersModalOpen(true);
    setEditingUserId(null);
    setNewPassword('');
    setPasswordSuccess(null);
    setIsAddingUser(false);
    try {
      setLoadingUsers(true);
      const res = await api.getCustomerUsers(cust.id);
      setCustomerUsers(res.items || []);
    } catch (err: any) {
      console.error(err);
      alert('Erro ao carregar usuários do cliente.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUpdatePassword = async (userId: string) => {
    if (!newPassword || newPassword.length < 6) {
      alert('A nova senha deve conter no mínimo 6 caracteres.');
      return;
    }
    setUpdatingPassword(true);
    setPasswordSuccess(null);
    try {
      await api.updateUserPassword(userId, newPassword);
      setPasswordSuccess(userId);
      setNewPassword('');
      setEditingUserId(null);
      setTimeout(() => setPasswordSuccess(null), 4000);
    } catch (err: any) {
      alert(err.message || 'Falha ao atualizar senha.');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleCreateNewUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    if (newUserPassword.length < 6) {
      alert('A senha deve ter no mínimo 6 caracteres.');
      return;
    }
    setSavingNewUser(true);
    try {
      await api.createCustomerUser(selectedCustomer.id, {
        email: newUserEmail,
        fullName: newUserName,
        password: newUserPassword,
        role: newUserRole,
      });
      setIsAddingUser(false);
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      const res = await api.getCustomerUsers(selectedCustomer.id);
      setCustomerUsers(res.items || []);
    } catch (err: any) {
      alert(err.message || 'Falha ao criar usuário.');
    } finally {
      setSavingNewUser(false);
    }
  };

  const handleSuspend = async (id: string) => {
    if (!confirm('Deseja suspender o acesso deste cliente?')) return;
    try {
      await api.suspendCustomer(id);
      loadCustomers();
    } catch (err: any) {
      alert(err.message || 'Erro ao suspender cliente.');
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await api.reactivateCustomer(id);
      loadCustomers();
    } catch (err: any) {
      alert(err.message || 'Erro ao reativar cliente.');
    }
  };

  const handleDeleteCustomer = async (cust: Customer) => {
    const displayName = cust.legalName || cust.fullName || cust.tradeName || cust.email;
    const confirmDelete = window.confirm(
      `ATENÇÃO: Deseja realmente excluir o cliente "${displayName}"?\n\nEsta operação é IRREVERSÍVEL. Todos os dados da instituição (projetos, aulas, quotas, assinaturas) e contas de login vinculadas serão permanentemente removidas do sistema e do Firebase Auth.`
    );
    if (!confirmDelete) return;

    try {
      await api.deleteCustomer(cust.id);
      alert('Cliente e todos os seus dados foram excluídos com sucesso.');
      loadCustomers();
    } catch (err: any) {
      alert(err.message || 'Erro ao excluir cliente.');
    }
  };


  const handleGrantBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomerId) return;
    try {
      await api.adjustQuota(selectedCustomerId, {
        bonus: Number(bonusAmount),
        adjustment: 0,
        reason: bonusReason,
      });
      alert('Bônus concedido com sucesso!');
      setIsBonusModalOpen(false);
      loadCustomers();
    } catch (err: any) {
      alert(err.message || 'Falha ao conceder bônus.');
    }
  };

  const filtered = customers.filter((c) => {
    const q = searchTerm.toLowerCase();
    const rawQ = searchTerm.replace(/\D/g, '');
    return (
      c.legalName?.toLowerCase().includes(q) ||
      c.fullName?.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.tradeName?.toLowerCase().includes(q) ||
      (c.cnpj && (c.cnpj.includes(q) || (rawQ && c.cnpj.replace(/\D/g, '').includes(rawQ)))) ||
      (c.cpf && (c.cpf.includes(q) || (rawQ && c.cpf.replace(/\D/g, '').includes(rawQ)))) ||
      (c.phone && (c.phone.includes(q) || (rawQ && c.phone.replace(/\D/g, '').includes(rawQ)))) ||
      (c.mobile && (c.mobile.includes(q) || (rawQ && c.mobile.replace(/\D/g, '').includes(rawQ))))
    );
  });

  return (
    <div>
      <Header
        title="Gestão de Clientes (Tenants)"
        subtitle="Controle cadastral, status de assinaturas e limites operacionais de cada instituição"
        action={
          <EduButton
            variant="primary"
            size="sm"
            onClick={() => setIsModalOpen(true)}
            icon={<PlusCircle size={16} />}
          >
            Cadastrar Cliente
          </EduButton>
        }
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {/* Search */}
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 text-txt-muted" size={16} />
            <input
              type="text"
              placeholder="Buscar por razão social, nome fantasia ou e-mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-surface border border-bdr rounded-md text-txt-primary placeholder:text-txt-muted focus:outline-none focus:border-brand-primary"
            />
          </div>
          <span className="text-xs text-txt-secondary">
            {filtered.length} clientes listados
          </span>
        </div>

        {/* Customers Table */}
        {loading ? (
          <div className="p-12 text-center text-xs text-txt-muted">Carregando clientes...</div>
        ) : filtered.length === 0 ? (
          <EduCard>
            <div className="text-center py-12">
              <Users size={36} className="mx-auto text-txt-muted mb-2" />
              <h3 className="text-sm font-semibold text-txt-primary">Nenhum cliente cadastrado</h3>
              <p className="text-xs text-txt-secondary mt-1">
                Cadastre sua primeira conta de cliente para permitir o acesso ao portal do cliente.
              </p>
              <EduButton
                variant="primary"
                size="sm"
                className="mt-4"
                onClick={() => setIsModalOpen(true)}
                icon={<PlusCircle size={14} />}
              >
                Cadastrar Primeiro Cliente
              </EduButton>
            </div>
          </EduCard>
        ) : (
          <div className="bg-surface border border-bdr rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-surface-secondary border-b border-bdr text-txt-secondary font-semibold">
                  <th className="py-3.5 px-4">Cliente / Instituição</th>
                  <th className="py-3.5 px-4">Documento</th>
                  <th className="py-3.5 px-4">Responsável</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4">Projetos / Aulas</th>
                  <th className="py-3.5 px-4 text-right">Ações Administrativas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bdr text-txt-primary">
                {filtered.map((cust) => (
                  <tr key={cust.id} className="hover:bg-surface-secondary/50 transition-colors">
                    <td className="py-3.5 px-4">
                      <strong className="block text-txt-primary text-sm">
                        {cust.tradeName || cust.legalName || cust.fullName}
                      </strong>
                      <span className="text-[11px] text-txt-secondary">{cust.email}</span>
                    </td>
                    <td className="py-3.5 px-4 font-mono text-txt-secondary text-xs">
                      {cust.cnpj ? maskCNPJ(cust.cnpj) : cust.cpf ? maskCPF(cust.cpf) : '—'}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="font-medium text-txt-primary">{cust.responsibleName}</span>
                      {cust.phone && <span className="block text-[11px] text-txt-muted">{maskPhone(cust.phone)}</span>}
                      {cust.mobile && <span className="block text-[11px] text-txt-muted">Cel: {maskPhone(cust.mobile)}</span>}
                    </td>
                    <td className="py-3.5 px-4">
                      {cust.status === 'active' ? (
                        <EduBadge variant="success" dot>Ativo</EduBadge>
                      ) : (
                        <EduBadge variant="error" dot>Suspenso</EduBadge>
                      )}
                    </td>
                    <td className="py-3.5 px-4 font-medium">
                      {cust.totalProjects || 0} proj • {cust.totalLessons || 0} aulas
                    </td>
                    <td className="py-3.5 px-4 text-right space-x-2">
                      <EduButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleOpenUsersModal(cust)}
                        icon={<Users size={12} className="text-txt-primary" />}
                      >
                        Usuários
                      </EduButton>

                      <EduButton
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setSelectedCustomerId(cust.id);
                          setIsBonusModalOpen(true);
                        }}
                        icon={<Award size={12} className="text-brand-primary" />}
                      >
                        Bônus
                      </EduButton>

                      {cust.status === 'active' ? (
                        <EduButton
                          variant="secondary"
                          size="sm"
                          onClick={() => handleSuspend(cust.id)}
                          icon={<UserX size={12} className="text-status-error" />}
                        >
                          Suspender
                        </EduButton>
                      ) : (
                        <EduButton
                          variant="primary"
                          size="sm"
                          onClick={() => handleReactivate(cust.id)}
                          icon={<UserCheck size={12} />}
                        >
                          Reativar
                        </EduButton>
                      )}

                      <EduButton
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteCustomer(cust)}
                        icon={<Trash2 size={12} />}
                      >
                        Excluir
                      </EduButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Cadastro de Cliente */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl border border-bdr shadow-xl max-w-lg w-full p-6">
            <h3 className="text-base font-bold text-txt-primary mb-4">Novo Cliente (Tenant)</h3>
            <form onSubmit={handleCreateCustomer} className="space-y-4">
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 text-xs font-semibold text-txt-primary cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    checked={type === 'PJ'}
                    onChange={() => {
                      setType('PJ');
                      setDocument('');
                    }}
                  />
                  Pessoa Jurídica (PJ)
                </label>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-txt-primary cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    checked={type === 'PF'}
                    onChange={() => {
                      setType('PF');
                      setDocument('');
                    }}
                  />
                  Pessoa Física (PF)
                </label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  {type === 'PJ' ? 'Razão Social *' : 'Nome Completo *'}
                </label>
                <input
                  type="text"
                  required
                  placeholder={type === 'PJ' ? 'Instituto Saber Educacional Ltda' : 'Prof. Carlos Eduardo'}
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              {type === 'PJ' && (
                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    Nome Fantasia
                  </label>
                  <input
                    type="text"
                    placeholder="Instituto Saber"
                    value={tradeName}
                    onChange={(e) => setTradeName(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    {type === 'PJ' ? 'CNPJ' : 'CPF'}
                  </label>
                  <input
                    type="text"
                    maxLength={type === 'PJ' ? 18 : 14}
                    placeholder={type === 'PJ' ? '00.000.000/0001-00' : '000.000.000-00'}
                    value={document}
                    onChange={(e) =>
                      setDocument(type === 'PJ' ? maskCNPJ(e.target.value) : maskCPF(e.target.value))
                    }
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary font-mono text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    Telefone Fixo
                  </label>
                  <input
                    type="text"
                    maxLength={15}
                    placeholder="(11) 3456-7890"
                    value={phone}
                    onChange={(e) => setPhone(maskPhone(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary font-mono text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    Celular / WhatsApp
                  </label>
                  <input
                    type="text"
                    maxLength={15}
                    placeholder="(11) 98765-4321"
                    value={mobile}
                    onChange={(e) => setMobile(maskPhone(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary font-mono text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    E-mail do Responsável *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="gestor@empresa.com.br"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Nome do Responsável *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Dr. Carlos Silva"
                  value={responsibleName}
                  onChange={(e) => setResponsibleName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    Senha de Acesso Inicial *
                  </label>
                  <input
                    type="password"
                    required
                    placeholder="Mínimo 6 caracteres"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    Confirmar Senha *
                  </label>
                  <input
                    type="password"
                    required
                    placeholder="Repita a senha"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <EduButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancelar
                </EduButton>
                <EduButton
                  type="submit"
                  variant="primary"
                  size="sm"
                  isLoading={submitting}
                >
                  Criar Cliente
                </EduButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Conceder Bônus */}
      {isBonusModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl border border-bdr shadow-xl max-w-sm w-full p-6">
            <h3 className="text-base font-bold text-txt-primary mb-2">Conceder Aulas Bônus</h3>
            <p className="text-xs text-txt-secondary mb-4">
              Aumenta o saldo disponível do cliente no ciclo vigente sem custos adicionais.
            </p>
            <form onSubmit={handleGrantBonus} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Quantidade de Aulas Bônus
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  max={100}
                  value={bonusAmount}
                  onChange={(e) => setBonusAmount(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Justificativa / Motivo
                </label>
                <input
                  type="text"
                  required
                  value={bonusReason}
                  onChange={(e) => setBonusReason(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <EduButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsBonusModalOpen(false)}
                >
                  Cancelar
                </EduButton>
                <EduButton
                  type="submit"
                  variant="primary"
                  size="sm"
                >
                  Confirmar Bônus
                </EduButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Listagem e Gestão de Senhas dos Usuários do Cliente */}
      {isUsersModalOpen && selectedCustomer && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl border border-bdr shadow-xl max-w-2xl w-full p-6 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-4 border-b border-bdr">
              <div>
                <h3 className="text-base font-bold text-txt-primary flex items-center gap-2">
                  <Users size={18} className="text-brand-primary" />
                  Usuários de {selectedCustomer.tradeName || selectedCustomer.legalName || selectedCustomer.fullName}
                </h3>
                <p className="text-xs text-txt-secondary mt-0.5">
                  Gerenciamento de credenciais e edição de senha no Firebase Authentication.
                </p>
              </div>
              <button
                onClick={() => setIsUsersModalOpen(false)}
                className="text-txt-muted hover:text-txt-primary p-1.5 rounded-md hover:bg-surface-secondary"
              >
                <X size={18} />
              </button>
            </div>

            <div className="my-4 flex items-center justify-between">
              <span className="text-xs font-semibold text-txt-secondary">
                {customerUsers.length} usuário(s) vinculado(s)
              </span>
              <EduButton
                variant={isAddingUser ? 'secondary' : 'primary'}
                size="sm"
                onClick={() => setIsAddingUser(!isAddingUser)}
                icon={isAddingUser ? <X size={14} /> : <UserPlus size={14} />}
              >
                {isAddingUser ? 'Fechar Formulário' : 'Novo Usuário'}
              </EduButton>
            </div>

            {/* Inline Add User Form */}
            {isAddingUser && (
              <form onSubmit={handleCreateNewUser} className="mb-4 p-4 bg-surface-secondary border border-bdr rounded-lg space-y-3">
                <h4 className="text-xs font-bold text-txt-primary">Cadastrar Novo Usuário para o Cliente</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-txt-secondary mb-1">Nome Completo *</label>
                    <input
                      type="text"
                      required
                      placeholder="Nome do colaborador"
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-bdr rounded bg-surface text-txt-primary focus:outline-none focus:border-brand-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-txt-secondary mb-1">E-mail de Login *</label>
                    <input
                      type="email"
                      required
                      placeholder="usuario@cliente.com.br"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-bdr rounded bg-surface text-txt-primary focus:outline-none focus:border-brand-primary"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-txt-secondary mb-1">Senha Inicial * (mín 6 caracteres)</label>
                    <input
                      type="password"
                      required
                      placeholder="••••••••"
                      value={newUserPassword}
                      onChange={(e) => setNewUserPassword(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-bdr rounded bg-surface text-txt-primary focus:outline-none focus:border-brand-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-txt-secondary mb-1">Papel / Permissão *</label>
                    <select
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value as any)}
                      className="w-full px-2.5 py-1.5 text-xs border border-bdr rounded bg-surface text-txt-primary focus:outline-none focus:border-brand-primary"
                    >
                      <option value="customer_admin">Gestor Geral (customer_admin)</option>
                      <option value="customer_user">Operador / Professor (customer_user)</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <EduButton type="button" variant="secondary" size="sm" onClick={() => setIsAddingUser(false)}>
                    Cancelar
                  </EduButton>
                  <EduButton type="submit" variant="primary" size="sm" isLoading={savingNewUser}>
                    Salvar Usuário
                  </EduButton>
                </div>
              </form>
            )}

            {/* Users Table / List */}
            <div className="overflow-y-auto flex-1 border border-bdr rounded-lg">
              {loadingUsers ? (
                <div className="p-8 text-center text-xs text-txt-muted">Carregando usuários vinculados...</div>
              ) : customerUsers.length === 0 ? (
                <div className="p-8 text-center text-xs text-txt-muted">
                  Nenhum usuário com login registrado diretamente para este cliente ainda.
                  <p className="mt-1 text-[11px] text-txt-secondary">
                    Clique em "Novo Usuário" acima para cadastrar a credencial de acesso.
                  </p>
                </div>
              ) : (
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-surface-secondary border-b border-bdr text-txt-secondary font-semibold">
                      <th className="py-2.5 px-3">Nome / Usuário</th>
                      <th className="py-2.5 px-3">E-mail</th>
                      <th className="py-2.5 px-3">Perfil</th>
                      <th className="py-2.5 px-3">Status</th>
                      <th className="py-2.5 px-3 text-right">Senha</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-bdr text-txt-primary">
                    {customerUsers.map((u) => (
                      <React.Fragment key={u.id}>
                        <tr className="hover:bg-surface-secondary/40 transition-colors">
                          <td className="py-2.5 px-3 font-semibold">{u.fullName || 'Usuário'}</td>
                          <td className="py-2.5 px-3 font-mono text-[11px] text-txt-secondary">{u.email}</td>
                          <td className="py-2.5 px-3">
                            <span className="font-semibold text-brand-primary text-[11px]">
                              {u.role === 'customer_admin' ? 'Gestor' : 'Operador'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3">
                            <EduBadge variant="success" size="sm" dot>Ativo</EduBadge>
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            {editingUserId === u.id ? (
                              <span className="text-[11px] text-txt-muted italic">Editando...</span>
                            ) : (
                              <div className="flex items-center justify-end gap-1.5">
                                {passwordSuccess === u.id && (
                                  <span className="text-[11px] text-status-success font-medium flex items-center gap-1">
                                    <Check size={12} /> Senha atualizada!
                                  </span>
                                )}
                                <EduButton
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setEditingUserId(u.id);
                                    setNewPassword('');
                                  }}
                                  icon={<KeyRound size={12} className="text-brand-primary" />}
                                >
                                  Editar Senha
                                </EduButton>
                              </div>
                            )}
                          </td>
                        </tr>

                        {/* Inline Password Edit Form */}
                        {editingUserId === u.id && (
                          <tr className="bg-orange-50/40 border-b border-orange-100">
                            <td colSpan={5} className="p-3">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 flex items-center gap-2">
                                  <Lock size={14} className="text-brand-primary flex-shrink-0" />
                                  <span className="text-xs font-semibold text-txt-primary">
                                    Nova Senha para {u.email}:
                                  </span>
                                  <input
                                    type="password"
                                    placeholder="Digite a nova senha (mín 6 dígitos)"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="px-2.5 py-1 text-xs border border-bdr rounded bg-surface w-64 focus:outline-none focus:border-brand-primary"
                                  />
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <EduButton
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      setEditingUserId(null);
                                      setNewPassword('');
                                    }}
                                  >
                                    Cancelar
                                  </EduButton>
                                  <EduButton
                                    variant="primary"
                                    size="sm"
                                    isLoading={updatingPassword}
                                    onClick={() => handleUpdatePassword(u.id)}
                                    icon={<Check size={12} />}
                                  >
                                    Salvar Senha
                                  </EduButton>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-bdr flex justify-end">
              <EduButton variant="secondary" size="sm" onClick={() => setIsUsersModalOpen(false)}>
                Fechar
              </EduButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
